// Hook listy pozycji oferty CRM – GET /sales-crm/clients/:cid/offers/:oid/positions

import { axios } from 'api';
import { useQuery, UseQueryOptions } from 'react-query';
import { ICommandResponseError as IError } from 'api/types';

/** Dostępność produktu w magazynie powiązana z konkretną parą produkt-jednostka. */
export interface ISalesCrmOfferPositionStock {
  warehouseId: number;
  name: string;
  quantity: number;
  productUnitId: number;
}

/** Produkt proponowany jako upsell dla pozycji oferty (zwracany przez backend). */
export interface ISalesCrmOfferPositionUpsellProduct {
  id: number;
  productName: string | null;
  symbol: string | null;
}

/** Pojedynczy próg cenowy: od ilości `qtyFrom` obowiązuje cena netto `price`. */
export interface IPriceThreshold {
  qtyFrom: number;
  price: number;
}

/** Pojedyncza pozycja oferty w response backendu */
export interface ISalesCrmOfferPosition {
  id: number;
  productId: number | null;
  productName: string | null;
  symbol: string | null;
  category: string | null;
  tags: string[] | null;
  imageUrl: string | null;
  quantity: number | null;
  unit: string | null;
  productUnitId: number | null;
  warehouseId: number | null;
  priceNetCatalog: number | null;
  priceNetList: number | null;
  priceNetSales: number | null;
  priceGrossCatalog: number | null;
  priceGrossList: number | null;
  priceGrossSales: number | null;
  discountPct: number | null;
  discountValue: number | null;
  marginPct: number | null;
  purchaseCostNet: number | null;
  minSalesNet: number | null;
  marginNet: number | null;
  marginNetTotal: number | null;
  ownCostsNet: number | null;
  quantityMultiplication: number | null;
  stocks: ISalesCrmOfferPositionStock[];
  upsellProducts: ISalesCrmOfferPositionUpsellProduct[];
  /**
   * Opcjonalne progi cenowe pozycji. Backend dziś nie zwraca tego pola — frontend
   * traktuje brak/`null` jako pozycję bez progów. Lista posortowana rosnąco po `qtyFrom`.
   */
  priceThresholds?: IPriceThreshold[] | null;
  /**
   * Tryb progów: false = klasyczne progi ilościowe (auto-apply przy ilości w koszyku),
   * true = warianty multiple-choice (klient akceptując ofertę wybiera jeden).
   * Sensowne tylko gdy `priceThresholds` nie-puste.
   */
  priceThresholdsAsVariants?: boolean;
  /**
   * Kalkulacja przypisana do pozycji (relacja 1:1 w UI). `null` = brak kalkulacji.
   * `isLinked: true` oznacza ze kalkulacja jest WSPOLDZIELONA (powiazana z innej pozycji
   * przez linkedCalculationId), `false` oznacza ze nalezy do tej pozycji.
   */
  calculation?: { id: number; name: string; isLinked: boolean } | null;
}

export interface ISalesCrmOfferPositionsResponse {
  items: ISalesCrmOfferPosition[];
  page: number;
  totalCount: number;
  totals: {
    valueNet: number;
    valueGross: number;
  };
}

export interface IGetPositionsParams {
  page?: number;
  limit?: number;
  versionId?: number | null;
}

const getSalesCrmOfferPositions = (
  clientId: number,
  offerId: string,
  params?: IGetPositionsParams
): Promise<ISalesCrmOfferPositionsResponse> => {
  const queryParams: Record<string, number> = {};
  if (params?.page) queryParams.page = params.page;
  if (params?.limit) queryParams.limit = params.limit;
  if (params?.versionId) queryParams.versionId = params.versionId;
  return axios.get(`/sales-crm/clients/${clientId}/offers/${offerId}/positions`, {
    params: queryParams
  });
};

export const useGetSalesCrmOfferPositions = (
  clientId: number,
  offerId: string,
  params?: IGetPositionsParams,
  options?: UseQueryOptions<ISalesCrmOfferPositionsResponse, IError>
) =>
  useQuery<ISalesCrmOfferPositionsResponse, IError>(
    ['sales-crm', 'client', clientId, 'offer', offerId, 'positions', params],
    () => getSalesCrmOfferPositions(clientId, offerId, params),
    {
      ...options,
      enabled:
        options?.enabled !== false && clientId > 0 && String(offerId).trim().length > 0
    }
  );
